import * as React from 'react'
import * as Cookies from 'js-cookie'
import dynamic from 'next/dynamic'
import gql from 'graphql-tag'

import { useApolloClient } from '@apollo/react-hooks'

import {
  useSiteDefinition,
  useConfig,
  useSiteConfig,
  useEnterpriseContext,
  useHorizonSessionSettings,
} from '@thg-commerce/enterprise-core'
import { createSubscribableRef } from '@thg-commerce/enterprise-utils'
import {
  BasketData,
  BasketVariables,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Basket/Basket'
import { Feature } from '@thg-commerce/enterprise-network/src/generated/graphql'

import { BasketContext } from './BasketContext'
import { BasketContextInterface } from './types'
import { setCookie } from '../../utils/CookieHelper'
import { COOKIE_GROUP_BASKET } from '../../utils/CookieHelper/types'

const AddedToBasketModalPresenter = dynamic<{}>(() =>
  import('../AddedToBasketModalPresenter').then(
    (mod) => mod.AddedToBasketModalPresenter,
  ),
)
const QuickBuyModalPresenter = dynamic<{}>(() =>
  import('../QuickBuyModalPresenter').then((mod) => mod.QuickBuyModalPresenter),
)

const ClickAndCollectModalPresenter = dynamic<{}>(() =>
  import(
    '@thg-commerce/enterprise-modal/src/ClickAndCollect/ClickAndCollectModalPresenter/ClickAndCollectModalPresenter'
  ).then((mod) => mod.ClickAndCollectModalPresenter),
)

const ClickAndCollectInfoModalPresenter = dynamic<{}>(() =>
  import(
    '@thg-commerce/enterprise-pages/src/Basket/ClickAndCollect/ClickAndCollectInfoModalPresenter/ClickAndCollectInfoModalPresenter'
  ).then((mod) => mod.ClickAndCollectInfoModalPresenter),
)

const BASKET_PAGE_QUERY = gql`
  query BasketPage(
    $id: ID
    $currency: Currency!
    $shippingDestination: Country!
    $productContentKeys: [String!]
    $wishlistEnabled: Boolean
    $loyaltyEnabled: Boolean
    $clickAndCollectEnabled: Boolean
  ) {
    basket(
      id: $id
      currency: $currency
      shippingDestination: $shippingDestination
      productContentKeys: $productContentKeys
      wishlistEnabled: $wishlistEnabled
      loyaltyEnabled: $loyaltyEnabled
      clickAndCollectEnabled: $clickAndCollectEnabled
      productDescriptionKeys: $productDescriptionKeys
    ) @client {
      id
      totalQuantity
      standardPrice {
        amount
      }
      discount {
        displayValue
        amount
      }
      appliedOffers {
        displayValue
        amount
        message
        removeable
      }
      items {
        id
        fulfilmentMethod
        product {
          sku
          title
          maxPerOrder
          externalIdentifier
          product {
            sku
            url
            variants {
              sku
            }
            images {
              largeProduct
            }
            brand {
              name
            }
          }
          inStock
          inWishlist
        }
        quantity
        chargePricePerUnit {
          displayValue
          amount
        }
        standardPricePerUnit {
          amount
          displayValue
        }
        totalChargePrice {
          displayValue
          amount
        }
        freeGift
        totalDiscount {
          displayValue
        }
        appliedOffers {
          totalBasketDiscount {
            displayValue
          }
          message
          info
        }
        messages {
          type
        }
        fulfilmentMethod
      }
      chargePrice {
        displayValue
        amount
      }
      messages {
        type
        message
      }
      recommendations {
        sku
        title
        price
        url
        image
        reviews {
          total
          averageScore
        }
        defaultVariant(
          options: {
            currency: $currency
            shippingDestination: $shippingDestination
          }
        ) {
          price(
            currency: $currency
            shippingDestination: $shippingDestination
          ) {
            price {
              amount
              displayValue
            }
            rrp {
              amount
              displayValue
            }
          }
        }
        cheapestVariant(
          currency: $currency
          shippingDestination: $shippingDestination
        ) {
          price(
            currency: $currency
            shippingDestination: $shippingDestination
          ) {
            price {
              amount
              displayValue
            }
            rrp {
              amount
              displayValue
            }
          }
        }
        mostExpensiveVariant(
          currency: $currency
          shippingDestination: $shippingDestination
        ) {
          price(
            currency: $currency
            shippingDestination: $shippingDestination
          ) {
            price {
              amount
              displayValue
            }
            rrp {
              amount
              displayValue
            }
          }
        }
      }
      selectYourSample {
        id
        title
        message
        currentAmountSpent {
          displayValue
          amount
        }
        tiers {
          id
          isFullyOutOfStock
          isPartiallyOutOfStock
          additionalSpendToReach {
            displayValue
            amount
          }
          thresholdAmountSpent {
            displayValue
          }
          products {
            images {
              thumbnail
              largeProduct
              zoom
            }
            title
            sku
            content(keys: $productContentKeys) {
              key
              value {
                ... on ProductContentRichContentListValue {
                  richContentListValue: value {
                    content {
                      content
                    }
                    __typename
                  }
                  __typename
                }
                ... on ProductContentRichContentValue {
                  richContentValue: value {
                    content {
                      content
                    }
                    __typename
                  }
                  __typename
                }
                ... on ProductContentStringListValue {
                  stringListValue: value
                  __typename
                }
                ... on ProductContentStringValue {
                  stringValue: value
                  __typename
                }
              }
            }
            descriptionContent
            inStock
          }
          selectedProducts {
            sku
          }
          maxSelectedProducts
        }
      }
      availablePaymentOptions {
        icon
        type
        subType
      }
      earnableLoyaltyPoints
    }
  }
`

export interface BasketRef {
  basket?: BasketData
  loading: boolean
}

const loadBasketId = (basketCookieKey: string) => {
  if (typeof window === 'undefined') {
    return undefined
  }

  const basketCookieValue = Cookies.get(basketCookieKey)
  if (typeof basketCookieValue === 'string') {
    return atob(basketCookieValue)
  }

  return undefined
}

export const BasketProvider: React.FunctionComponent<{
  children: React.ReactNode
}> = (props) => {
  const { basketCookieKey } = useConfig()
  const { domain } = useSiteDefinition()
  const basketIdRef = createSubscribableRef<string | undefined>(
    loadBasketId(basketCookieKey),
  )
  const basketRef = createSubscribableRef<BasketRef>({
    basket: undefined,
    loading: true,
  })

  const client = useApolloClient()

  const {
    value: [getBasketId],
    subscribe: basketIdChanged,
    unsubscribe: unsubscribeBasketIdChanged,
  } = basketIdRef

  const persistBasketId = (newBasketId: string | undefined) => {
    if (typeof window === 'undefined' || typeof newBasketId !== 'string') {
      return
    }

    const cookieExpires = new Date()
    cookieExpires.setDate(cookieExpires.getDate() + 30)

    setCookie({
      domain,
      name: basketCookieKey,
      value: btoa(newBasketId),
      group: COOKIE_GROUP_BASKET,
      expires: cookieExpires,
      path: '/',
    })
  }

  React.useEffect(() => {
    fetchBasket(getBasketId())
  }, [])

  React.useEffect(() => {
    basketIdChanged(persistBasketId)
    basketIdChanged(fetchBasket)

    return () => {
      unsubscribeBasketIdChanged(persistBasketId)
      unsubscribeBasketIdChanged(fetchBasket)
    }
  }, [])

  const {
    horizonFeatures,
    appConfig: { productDescriptionKeys },
    requestConfig: { applyCodeToBasketState },
  } = useEnterpriseContext()

  const {
    productDetailKeyAdditionList,
    enableWishlists,
    enableWishlistsGlobal,
    enableWishlistOnBasketItem,
  } = useSiteConfig()

  const loyaltyEnabled = horizonFeatures?.includes(Feature.Loyalty) || false
  const clickAndCollectEnabled =
    horizonFeatures?.includes(Feature.ClickAndCollect) || false

  const wishlistEnabled =
    (enableWishlists &&
      enableWishlistsGlobal &&
      enableWishlistOnBasketItem &&
      horizonFeatures?.includes(Feature.Wishlist)) ||
    false

  const sessionSettings = useHorizonSessionSettings()

  const {
    value: [_, setBasket],
  } = basketRef

  const fetchBasket = (newBasketId: string | undefined) => {
    client
      .query<{ basket: BasketData }, BasketVariables>({
        query: BASKET_PAGE_QUERY,

        variables: {
          loyaltyEnabled,
          wishlistEnabled,
          clickAndCollectEnabled,
          productDescriptionKeys,
          ...sessionSettings,
          id: newBasketId,
          productContentKeys: [
            ...(productDescriptionKeys?.items || []),
            ...(productDescriptionKeys?.details || []),
            ...(productDescriptionKeys?.video || []),
            ...(productDetailKeyAdditionList || []),
          ],
        },
      })
      .then((response) => {
        setBasket({ basket: response.data.basket, loading: false })
        persistBasketId(response.data.basket.id)
      })
      .catch((e) =>
        console.warn(`Failed to load basket with error ${e.message}`),
      )
  }

  const value: BasketContextInterface = {
    basketIdRef,
    basketRef,
    applyCodeToBasketState,
    presentAddedToBasketModal: React.useRef((_: string) => {}),
    presentQuickBuyModal: React.useRef((_: string) => {}),
    presentClickAndCollectModal: React.useRef(() => {}),
    presentClickAndCollectInfoModal: React.useRef(() => {}),
  }

  return (
    <BasketContext.Provider value={value}>
      {props.children}
      <AddedToBasketModalPresenter />
      <QuickBuyModalPresenter />
      <ClickAndCollectModalPresenter />
      <ClickAndCollectInfoModalPresenter />
    </BasketContext.Provider>
  )
}
